// Forge : arène libre à part où chacun joue avec un tank amélioré au hangar (choix de Léni, 5 novembre 2026,
// détail dans docs/FORGE.md). Les améliorations ne comptent qu'en Forge : les autres modes restent équitables.
// La puissance ne s'achète jamais avec de l'argent réel : niveaux et coffres se paient en pièces gagnées en jouant.
import { RARITIES, type Rarity } from './cosmetics';

export type ForgeUpgradeId = 'blindage' | 'canon' | 'chargeur' | 'moteur' | 'belier' | 'reparation';
export const FORGE_UPGRADES: ForgeUpgradeId[] = ['blindage', 'canon', 'chargeur', 'moteur', 'belier', 'reparation'];
export const FORGE_MAX_LEVEL = 10;
export const FORGE_MAX_POWER = FORGE_UPGRADES.length * FORGE_MAX_LEVEL;

/** Gain par niveau : +3 %, sauf le moteur (+1,5 %) pour garder la maniabilité. */
export const FORGE_STEP: Record<ForgeUpgradeId, number> = { blindage: 0.03, canon: 0.03, chargeur: 0.03, moteur: 0.015, belier: 0.03, reparation: 0.03 };

export type ForgeLevels = Record<ForgeUpgradeId, number>;

export function emptyLevels(): ForgeLevels {
  return { blindage: 0, canon: 0, chargeur: 0, moteur: 0, belier: 0, reparation: 0 };
}

/** Niveaux bornés à 0–10 (entiers). */
export function clampLevels(raw: Partial<Record<string, unknown>> | null | undefined): ForgeLevels {
  const out = emptyLevels();
  for (const id of FORGE_UPGRADES) {
    const v = raw?.[id];
    out[id] = typeof v === 'number' && Number.isFinite(v) ? Math.max(0, Math.min(FORGE_MAX_LEVEL, Math.floor(v))) : 0;
  }
  return out;
}

/** Puissance = somme des niveaux (0 à 60). */
export function forgePower(l: ForgeLevels): number {
  return FORGE_UPGRADES.reduce((s, id) => s + l[id], 0);
}

/** Multiplicateur d'une amélioration à un niveau donné. */
export function forgeMul(id: ForgeUpgradeId, level: number): number {
  return 1 + FORGE_STEP[id] * Math.max(0, Math.min(FORGE_MAX_LEVEL, level));
}

/** Bonus appliqués au tank (1 = aucun). */
export interface ForgeBonus {
  hp: number;
  dmg: number;
  reload: number;
  speed: number;
  body: number;
  regen: number;
}

export const NO_FORGE_BONUS: ForgeBonus = { hp: 1, dmg: 1, reload: 1, speed: 1, body: 1, regen: 1 };

export function forgeBonus(l: ForgeLevels): ForgeBonus {
  return {
    hp: forgeMul('blindage', l.blindage),
    dmg: forgeMul('canon', l.canon),
    reload: forgeMul('chargeur', l.chargeur),
    speed: forgeMul('moteur', l.moteur),
    body: forgeMul('belier', l.belier),
    regen: forgeMul('reparation', l.reparation),
  };
}

/** Coût du niveau `level` (1 à 10) : n × 10 pièces détachées + n × 150 pièces. */
export function levelCost(level: number): { parts: number; coins: number } {
  return { parts: level * 10, coins: level * 150 };
}

/** Pièces détachées encore utiles à une amélioration pour atteindre le niveau 10, compte tenu du stock. */
export function partsStillNeeded(level: number, stock: number): number {
  let need = 0;
  for (let n = level + 1; n <= FORGE_MAX_LEVEL; n++) need += levelCost(n).parts;
  return Math.max(0, need - stock);
}

// ─── Coffres de puissance ───────────────────────────────────────────────

export interface ForgeChestDef {
  rarity: Rarity;
  /** Pièces détachées données, réparties à parts égales entre `upgrades` améliorations tirées au hasard. */
  parts: number;
  upgrades: number;
  /** Prix en pièces gagnées en jouant ; null = ne se gagne qu'en jouant. */
  price: number | null;
}

export const FORGE_CHESTS: Record<Rarity, ForgeChestDef> = {
  commun: { rarity: 'commun', parts: 10, upgrades: 1, price: null },
  rare: { rarity: 'rare', parts: 30, upgrades: 2, price: 1500 },
  epique: { rarity: 'epique', parts: 80, upgrades: 3, price: 4000 },
  legendaire: { rarity: 'legendaire', parts: 200, upgrades: 4, price: 10000 },
  mythique: { rarity: 'mythique', parts: 500, upgrades: 6, price: null },
};

/** Chances du coffre gagné en jouant (rareté tirée à l'ouverture), en pour mille. */
export const FORGE_EARNED_ODDS: Record<Rarity, number> = { commun: 600, rare: 270, epique: 100, legendaire: 27, mythique: 3 };
if (RARITIES.reduce((s, r) => s + FORGE_EARNED_ODDS[r], 0) !== 1000) throw new Error('chances du coffre de Forge : total différent de 1000 ‰');

/** Pièces rendues par pièce détachée quand toutes les améliorations sont déjà couvertes. */
export const FORGE_PART_REFUND = 15;

/** Coffres gagnés en jouant : un toutes les 10 minutes actives, 6 par jour (UTC) ; un Épique par boss vaincu. */
export const FORGE_MINUTES_PER_CHEST = 10;
export const FORGE_TIME_CHESTS_PER_DAY = 6;
export const FORGE_BOSS_CHEST: Rarity = 'epique';

/** Regroupement : écart de puissance maximal entre un joueur et la moyenne d'une arène. */
export const FORGE_MATCH_GAP = 10;

/** Rareté tirée pour un coffre gagné, à partir d'un entier `roll` uniforme dans [0, 1000). */
export function earnedRarity(roll: number): Rarity {
  let acc = 0;
  for (const r of RARITIES) {
    acc += FORGE_EARNED_ODDS[r];
    if (roll < acc) return r;
  }
  throw new Error('tirage hors bornes');
}

/**
 * Contenu d'un coffre : les améliorations encore à couvrir (pièces détachées utiles) sont tirées sans remise, chacune
 * avec la même chance, puis les pièces détachées sont réparties à parts égales (le reste va aux premières tirées).
 * Si aucune amélioration n'en a plus besoin, le coffre est rendu en pièces. `rand(n)` rend un entier uniforme dans [0, n).
 */
export function rollChestContent(
  chest: ForgeChestDef,
  levels: ForgeLevels,
  stock: Record<ForgeUpgradeId, number>,
  rand: (n: number) => number,
): { parts: Partial<Record<ForgeUpgradeId, number>>; refund: number } {
  const open = FORGE_UPGRADES.filter((id) => partsStillNeeded(levels[id], stock[id]) > 0);
  if (open.length === 0) return { parts: {}, refund: chest.parts * FORGE_PART_REFUND };
  const picked: ForgeUpgradeId[] = [];
  const bag = [...open];
  const k = Math.min(chest.upgrades, bag.length);
  for (let i = 0; i < k; i++) picked.push(bag.splice(rand(bag.length), 1)[0]);
  const base = Math.floor(chest.parts / k);
  let rest = chest.parts - base * k;
  const parts: Partial<Record<ForgeUpgradeId, number>> = {};
  for (const id of picked) {
    parts[id] = base + (rest > 0 ? 1 : 0);
    if (rest > 0) rest--;
  }
  return { parts, refund: 0 };
}

/** Niveaux d'un bot de Forge : la puissance visée répartie entre les améliorations (bornée à 0–60). */
export function botLevels(power: number, rand: (n: number) => number): ForgeLevels {
  const l = emptyLevels();
  let left = Math.max(0, Math.min(FORGE_MAX_POWER, Math.round(power)));
  const base = Math.floor(left / FORGE_UPGRADES.length);
  for (const id of FORGE_UPGRADES) l[id] = base;
  left -= base * FORGE_UPGRADES.length;
  const bag = FORGE_UPGRADES.filter((id) => l[id] < FORGE_MAX_LEVEL);
  while (left > 0 && bag.length) {
    const id = bag.splice(rand(bag.length), 1)[0];
    l[id]++;
    left--;
  }
  return l;
}

/** Codes des soldes (registre existant, aucune migration). */
export const FORGE_CUR = {
  level: (id: ForgeUpgradeId) => `pw_lvl_${id}` as const,
  parts: (id: ForgeUpgradeId) => `pw_part_${id}` as const,
  /** Coffres gagnés au temps, rareté tirée à l'ouverture. */
  box: 'pw_box' as const,
  /** Coffres gagnés sur un boss, rareté fixe (Épique). */
  bossBox: 'pw_box_boss' as const,
  /** Minutes actives en Forge depuis le dernier coffre de temps (0 à 9). */
  minutes: 'pw_min' as const,
};
